import { useCallback, useEffect, useState } from "react";

import { Link } from "react-router";

import { ProductChip, StockCount } from "@/components/chips";
import { Field, Section, input } from "@/components/form";
import { ColourEditor, FeaturePicker, ImageManager } from "@/components/product-media";
import * as products from "@/lib/api/products";
import { fetchCategories } from "@/lib/api/catalogue";
import { ApiError } from "@/lib/api-client";
import { Pager, Select } from "@/routes/orders";
import type { AdminProduct, AdminProductDetail, ApiCategory, ProductStatus } from "@/lib/api/types";
import { cn, formatDateTime, formatMinor } from "@/lib/utils";

/**
 * Products — the catalogue as the operator owns it, not as a shopper sees it.
 *
 * Two differences from `/v1/products`, and both are the point of the screen:
 * the exact `stockQuantity` (the public surface carries two booleans and never
 * the count, PLAN.md §4.2.1), and DRAFT and ARCHIVED rows, which are invisible
 * to the storefront entirely.
 *
 * ── Why editing is split across three forms ────────────────────────────────
 *
 * Because the API splits it, and the API splits it because two of the three
 * owe an audit row. A price change appends to `PriceHistory`; a stock change
 * appends to `StockMovement`; a reworded spec sheet appends to nothing. One
 * combined "save product" form would route all three through a single endpoint,
 * and the audited half would eventually slip through unaudited. Stock is not
 * even on this screen — it has its own, because it is the only writer.
 */

type State =
  | { phase: "loading" }
  | { phase: "ready"; rows: AdminProduct[]; total: number; pages: number }
  | { phase: "error"; message: string };

const STATUSES: ProductStatus[] = ["ACTIVE", "DRAFT", "ARCHIVED"];

export function ProductsRoute() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ProductStatus | "">("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"position" | "stock" | "name" | "updated">("position");
  const [state, setState] = useState<State>({ phase: "loading" });
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  /**
   * `?open=<slug>` expands one row on arrival. The create form navigates here
   * with it set, because the next thing to do after creating a product is
   * always to add its photographs — and hunting for the row you just made in a
   * paginated table is a small insult.
   *
   * Read once, into state, rather than driven by the URL: the operator
   * collapsing the row should not be undone by a re-render, and leaving the
   * parameter in the address bar is harmless.
   */
  const [open, setOpen] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("open"),
  );
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await products.fetchProducts(
          {
            page,
            status: status || undefined,
            category: category || undefined,
            search: search.trim() || undefined,
            sort,
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        setState({
          phase: "ready",
          rows: result.data,
          total: result.meta.total,
          pages: Math.max(1, result.meta.pages),
        });
      } catch (thrown) {
        if (controller.signal.aborted) return;

        setState({
          phase: "error",
          message: thrown instanceof ApiError ? thrown.message : "Could not reach the API.",
        });
      }
    })();

    return () => controller.abort();
  }, [page, status, category, search, sort, reload]);

  /**
   * The category filter reads the public endpoint, and that is fine here: a
   * category is a nav label with no stock, no status and nothing withheld from
   * the storefront. There is no admin variant to prefer.
   */
  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const list = await fetchCategories(controller.signal);
        if (!controller.signal.aborted) setCategories(list);
      } catch {
        // A missing filter is a smaller loss than a screen that will not load.
      }
    })();

    return () => controller.abort();
  }, []);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div>
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-ink">Products</h1>
          <p className="mt-1 text-[13px] text-muted">
            {state.phase === "ready"
              ? `${state.total} product${state.total === 1 ? "" : "s"}, including drafts and archived`
              : state.phase === "loading"
                ? "Loading…"
                : "Could not load products"}
          </p>
        </div>

        <Link
          to="/products/new"
          className="shrink-0 rounded-lg bg-ink px-3.5 py-2 text-[13px] font-semibold text-white"
        >
          New product
        </Link>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Name or slug…"
          className="w-[220px] rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
        />

        <Select
          value={status}
          onChange={(value) => {
            setStatus(value as ProductStatus | "");
            setPage(1);
          }}
          options={[
            { value: "", label: "All statuses" },
            { value: "ACTIVE", label: "Live" },
            { value: "DRAFT", label: "Draft" },
            { value: "ARCHIVED", label: "Archived" },
          ]}
        />

        <Select
          value={category}
          onChange={(value) => {
            setCategory(value);
            setPage(1);
          }}
          options={[
            { value: "", label: "All categories" },
            ...categories.map((item) => ({ value: item.slug, label: item.name })),
          ]}
        />

        <Select
          value={sort}
          onChange={(value) => {
            setSort(value as typeof sort);
            setPage(1);
          }}
          options={[
            { value: "position", label: "Storefront order" },
            { value: "stock", label: "Lowest stock first" },
            { value: "name", label: "Name" },
            { value: "updated", label: "Recently changed" },
          ]}
        />
      </div>

      {state.phase === "error" ? (
        <p className="rounded-lg bg-bad-tint px-4 py-3 text-[13px] text-bad">{state.message}</p>
      ) : state.phase === "loading" ? (
        <p className="text-[13.5px] text-muted">Loading products…</p>
      ) : state.rows.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-4 py-8 text-center text-[13.5px] text-muted">
          No products match those filters.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-line bg-surface">
            <table className="w-full border-collapse text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] tracking-wide text-faint uppercase">
                  <th className="px-4 py-3 font-semibold">Product</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold">Stock</th>
                </tr>
              </thead>
              <tbody>
                {state.rows.map((product) => (
                  <ProductRow
                    key={product.slug}
                    product={product}
                    categories={categories}
                    expanded={open === product.slug}
                    onToggle={() =>
                      setOpen((current) => (current === product.slug ? null : product.slug))
                    }
                    onChanged={refresh}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <Pager page={page} pages={state.pages} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function ProductRow({
  product,
  categories,
  expanded,
  onToggle,
  onChanged,
}: {
  product: AdminProduct;
  categories: ApiCategory[];
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          "cursor-pointer border-b border-line last:border-0 hover:bg-canvas",
          expanded && "bg-canvas",
        )}
      >
        <td className="px-4 py-3">
          <span className="block font-medium text-ink">{product.name}</span>
          <span className="block text-[11.5px] text-faint">{product.slug}</span>
        </td>
        <td className="px-4 py-3 text-muted">{product.category.name}</td>
        <td className="px-4 py-3">
          <ProductChip status={product.status} />
        </td>
        <td className="px-4 py-3 text-right">
          <span className="block font-medium text-ink tabular-nums">
            {formatMinor(product.price.minor)}
          </span>
          <span className="block text-[11.5px] text-faint tabular-nums line-through">
            {formatMinor(product.mrp.minor)}
          </span>
        </td>
        <td className="px-4 py-3">
          <StockCount product={product} />
        </td>
      </tr>

      {expanded ? (
        <tr className="border-b border-line last:border-0">
          <td colSpan={5} className="bg-canvas px-4 py-4">
            <ProductDetail slug={product.slug} categories={categories} onChanged={onChanged} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The expanded row. Fetched separately rather than expanded from the list row,
 * because the detail carries the recent ledger and price history — which would
 * be most of the payload if the list carried it for every row.
 */
function ProductDetail({
  slug,
  categories,
  onChanged,
}: {
  slug: string;
  categories: ApiCategory[];
  onChanged: () => void;
}) {
  const [product, setProduct] = useState<AdminProductDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const fresh = await products.fetchProduct(slug, controller.signal);
        if (!controller.signal.aborted) setProduct(fresh);
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load this product.");
        }
      }
    })();

    return () => controller.abort();
  }, [slug]);

  function applied(fresh: AdminProductDetail) {
    setProduct(fresh);
    onChanged();
  }

  if (error && !product) return <p className="text-[13px] text-bad">{error}</p>;
  if (!product) return <p className="text-[13px] text-muted">Loading…</p>;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div>
        <DetailsForm product={product} categories={categories} onApplied={applied} />
        <PriceForm product={product} onApplied={applied} />
        <ImageManager product={product} onApplied={applied} />
        <ColourEditor
          colours={product.colours ?? []}
          onSave={async (next) => applied(await products.replaceColours(slug, next))}
        />
        <FeaturePicker
          selected={product.features ?? []}
          onSave={async (next) => applied(await products.replaceFeatures(slug, next))}
        />
      </div>

      <div>
        <Section title="Stock">
          <p className="mb-2 text-[12.5px] text-body">
            <span className="text-[18px] font-bold text-ink tabular-nums">
              {product.stockQuantity}
            </span>
            <span className="ml-2 text-faint">on hand, low at {product.lowStockThreshold}</span>
          </p>
          {/* Deliberately a pointer, not a form. `adjustStock()` is the single
              writer for this column, and a second place to type a number into
              it is how a panel grows a second one. */}
          <p className="text-[11.5px] leading-relaxed text-faint">
            Inventory changes happen on the Stock screen, where every change writes a ledger row.
            The threshold above is editable here.
          </p>
        </Section>

        {/**
         * Called out rather than left to be noticed on the form.
         *
         * A missing HSN code has no visible effect until somebody who has paid
         * clicks Download invoice and is told the order cannot be invoiced.
         * Saying so here, on the screen where it is fixed, is the difference
         * between finding out now and finding out from a customer.
         */}
        {product.hsnCode === null || product.gstRateBp === null ? (
          <Section title="Not invoiceable">
            <p className="text-[11.5px] leading-relaxed text-faint">
              This product has no {product.hsnCode === null ? "HSN code" : "GST rate"}, so any order
              containing it cannot be given a tax invoice. Fill it in above — it applies to future
              orders only, and never changes an invoice already issued.
            </p>
          </Section>
        ) : null}

        {/**
         * The same call-out, for the courier. A missing weight has no visible
         * effect until an operator opens a paid order, presses Book shipment
         * and is told to go and weigh something — usually while a customer is
         * waiting.
         */}
        {!product.shippable ? (
          <Section title="Not shippable">
            <p className="text-[11.5px] leading-relaxed text-faint">
              This product has no shipping weight or carton, so no parcel containing it can be
              booked with the courier. Fill both in above.
            </p>
          </Section>
        ) : null}

        {product.priceHistory.length > 0 ? (
          <Section title="Price history">
            <ul className="flex flex-col gap-1.5">
              {product.priceHistory.map((change) => (
                <li key={change.id} className="flex flex-wrap gap-x-2 text-[12px]">
                  <span className="w-[110px] shrink-0 text-faint tabular-nums">
                    {formatDateTime(change.createdAt)}
                  </span>
                  <span className="font-medium text-ink tabular-nums">
                    {change.price.formatted}
                  </span>
                  <span className="text-faint">{change.discountLabel}</span>
                  {change.note ? (
                    <span className="w-full truncate text-[11.5px] text-muted">{change.note}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {product.stockLog.length > 0 ? (
          <Section title="Recent movements">
            <ul className="flex flex-col gap-1">
              {product.stockLog.slice(0, 6).map((movement) => (
                <li key={movement.id} className="flex gap-2 text-[11.5px]">
                  <span className="w-[105px] shrink-0 text-faint tabular-nums">
                    {formatDateTime(movement.createdAt)}
                  </span>
                  <span
                    className={cn(
                      "w-[34px] shrink-0 text-right font-semibold tabular-nums",
                      movement.delta > 0 ? "text-good" : "text-bad",
                    )}
                  >
                    {movement.delta > 0 ? `+${movement.delta}` : movement.delta}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-muted">
                    {movement.orderNumber ?? movement.note ?? movement.reason.toLowerCase()}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}
      </div>
    </div>
  );
}

/** Name, prose, status, position — everything that owes no audit row. */
function DetailsForm({
  product,
  categories,
  onApplied,
}: {
  product: AdminProductDetail;
  categories: ApiCategory[];
  onApplied: (fresh: AdminProductDetail) => void;
}) {
  /**
   * Only the fields the operator has touched. No reset effect is needed: the
   * table keys each row by slug, so expanding a different product unmounts this
   * form and mounts a fresh one — half-typed edits cannot follow it across.
   */
  const [edit, setEdit] = useState<products.ProductEdit>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /**
   * Only what changed is sent. A patch that echoed every field back would
   * overwrite a colleague's concurrent edit to a field this operator never
   * touched — last-write-wins across the whole row rather than the one field.
   */
  const set = <K extends keyof products.ProductEdit>(key: K, value: products.ProductEdit[K]) =>
    setEdit((current) => ({ ...current, [key]: value }));

  const dirty = Object.keys(edit).length > 0;

  /**
   * Publishing needs a photograph, and the API refuses without one.
   *
   * Mirrored here so the operator learns it while choosing, not after saving —
   * the panel is the only place the two facts (this status, that gallery) are
   * on screen together. The server check is still the rule; this is the
   * courtesy.
   */
  const wouldPublish = (edit.status ?? product.status) === "ACTIVE" && product.status !== "ACTIVE";
  const blockedByImages = wouldPublish && (product.images?.length ?? 0) === 0;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!dirty || busy || blockedByImages) return;

    setBusy(true);
    setError(null);
    setSaved(false);

    try {
      onApplied(await products.updateProduct(product.slug, edit));
      setEdit({});
      setSaved(true);
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "Those changes were not saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => void save(event)}
      className="mb-4 rounded-lg border border-line bg-surface p-3"
    >
      <h3 className="mb-3 text-[11px] font-bold tracking-wide text-faint uppercase">Details</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name">
          <input
            type="text"
            value={edit.name ?? product.name}
            onChange={(event) => set("name", event.target.value)}
            className={input}
          />
        </Field>

        <Field label="Eyebrow" hint="The label above the name on a card">
          <input
            type="text"
            value={edit.eyebrow ?? product.eyebrow}
            onChange={(event) => set("eyebrow", event.target.value)}
            className={input}
          />
        </Field>

        <Field label="Card spec" hint="The one-line spec under the name">
          <input
            type="text"
            value={edit.spec ?? product.spec}
            onChange={(event) => set("spec", event.target.value)}
            className={input}
          />
        </Field>

        <Field label="Badge" hint="BESTSELLER, NEW, or empty">
          <input
            type="text"
            value={edit.badge ?? product.badge}
            onChange={(event) => set("badge", event.target.value)}
            className={input}
          />
        </Field>

        <Field
          label="Status"
          hint={
            blockedByImages
              ? "Add at least one image below before making this product live."
              : "Draft and archived leave the storefront at once"
          }
        >
          <select
            value={edit.status ?? product.status}
            onChange={(event) => set("status", event.target.value as ProductStatus)}
            className={input}
          >
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value === "ACTIVE" ? "Live" : value === "DRAFT" ? "Draft" : "Archived"}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Category">
          <select
            value={edit.category ?? product.category.slug}
            onChange={(event) => set("category", event.target.value)}
            className={input}
          >
            {/* Falls back to the product's own category when the list failed to
                load, so the select never renders blank and "saves" a move. */}
            {(categories.length > 0
              ? categories
              : [{ slug: product.category.slug, name: product.category.name }]
            ).map((item) => (
              <option key={item.slug} value={item.slug}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Low-stock threshold" hint="Drives the restock report and 'Only 3 left'">
          <input
            type="number"
            min={0}
            step={1}
            value={edit.lowStockThreshold ?? product.lowStockThreshold}
            onChange={(event) => set("lowStockThreshold", Number(event.target.value))}
            className={input}
          />
        </Field>

        <Field label="Home row" hint="Empty keeps it off the curated row">
          <input
            type="number"
            min={0}
            step={1}
            value={
              (edit.homePosition === undefined ? product.homePosition : edit.homePosition) ?? ""
            }
            onChange={(event) =>
              // An empty box is `null`, not `0` — position zero is the *first*
              // slot on the home row, which is the opposite of removing it.
              set("homePosition", event.target.value === "" ? null : Number(event.target.value))
            }
            className={input}
          />
        </Field>

        {/**
         * The tax fields. Both are required before this product's orders can be
         * given a tax invoice, and both are empty on everything that predates
         * invoicing — so the hints say what filling them in is *for*, rather
         * than restating the label.
         *
         * Editing these changes future orders only. Every order already placed
         * carries its own frozen copy of the code and rate it was sold at,
         * which is what makes an old invoice reprint identically.
         */}
        <Field label="HSN code" hint="4–8 digits. Without it, orders cannot be invoiced">
          <input
            inputMode="numeric"
            placeholder="85183000"
            value={(edit.hsnCode === undefined ? product.hsnCode : edit.hsnCode) ?? ""}
            // Empty is `null`, not `""` — the API rejects a blank string and
            // null is what actually clears the column.
            onChange={(event) => set("hsnCode", event.target.value.trim() || null)}
            className={input}
          />
        </Field>

        <Field label="GST rate" hint="Percent — 18, 12, 5. Stored as basis points">
          <input
            type="number"
            min={0}
            max={28}
            step={0.5}
            value={(() => {
              // `undefined` means untouched, so the saved value shows; `null`
              // means the operator has cleared the box, which is a real edit
              // and must not fall back to what is stored.
              const current = edit.gstRateBp === undefined ? product.gstRateBp : edit.gstRateBp;
              return current === null ? "" : current / 100;
            })()}
            onChange={(event) =>
              // Shown as a percentage because that is what a rate notification
              // says; stored in basis points because the tax arithmetic is
              // integer-only. 18 → 1800, and 2.5 → 250 without a float.
              set(
                "gstRateBp",
                event.target.value === "" ? null : Math.round(Number(event.target.value) * 100),
              )
            }
            className={input}
          />
        </Field>

        {/**
         * The physical product, for courier booking.
         *
         * Same posture as the tax fields above and the same reason: a defaulted
         * weight is a wrong declaration nobody notices until iCarry re-weighs
         * the box and bills the difference. Empty is `null`, which is a refusal
         * to book — loud, and fixable by one person with a scale.
         *
         * Unlike the tax fields these are NOT frozen onto the order. What gets
         * frozen is the parcel as booked, because the courier was told about a
         * box rather than a basket of products.
         */}
        <Field label="Weight" hint="Grams, packaged. Without it, no parcel can be booked">
          <input
            type="number"
            min={1}
            step={1}
            placeholder="500"
            value={(edit.weightGrams === undefined ? product.weightGrams : edit.weightGrams) ?? ""}
            onChange={(event) =>
              set("weightGrams", event.target.value === "" ? null : Number(event.target.value))
            }
            className={input}
          />
        </Field>

        <Field label="Carton (mm)" hint="Length × breadth × height. All three, or none">
          <div className="flex gap-1.5">
            {(["lengthMm", "breadthMm", "heightMm"] as const).map((side) => (
              <input
                key={side}
                type="number"
                min={1}
                step={1}
                placeholder={side === "lengthMm" ? "L" : side === "breadthMm" ? "B" : "H"}
                value={(edit[side] === undefined ? product[side] : edit[side]) ?? ""}
                onChange={(event) =>
                  set(side, event.target.value === "" ? null : Number(event.target.value))
                }
                className={input}
              />
            ))}
          </div>
        </Field>
      </div>

      <Field label="Description">
        <textarea
          rows={3}
          value={edit.description ?? product.description ?? ""}
          onChange={(event) => set("description", event.target.value)}
          className={cn(input, "resize-y")}
        />
      </Field>

      <Field label="Spec sheet" hint="The long ` · `-joined spec line">
        <textarea
          rows={2}
          value={edit.specSheet ?? product.specSheet ?? ""}
          onChange={(event) => set("specSheet", event.target.value)}
          className={cn(input, "resize-y")}
        />
      </Field>

      {error ? <p className="mt-2 text-[12px] text-bad">{error}</p> : null}
      {saved && !dirty ? <p className="mt-2 text-[12px] text-good">Saved.</p> : null}

      <button
        type="submit"
        disabled={!dirty || busy || blockedByImages}
        className="mt-3 rounded-lg bg-ink px-3 py-2 text-[12.5px] font-semibold text-white disabled:opacity-40"
      >
        {busy ? "Saving…" : "Save details"}
      </button>
    </form>
  );
}

/**
 * The price form.
 *
 * In rupees on screen and paise on the wire, converted once here — the API
 * takes integer paise and rejects a fraction, because a float anywhere near
 * currency is how this codebase would acquire its first rounding bug.
 *
 * The discount label is typed, never computed. The distributor sheet labels
 * every line "25% off" where the MRP arithmetic gives 20%, and the storefront
 * carries the sheet's number verbatim — so deriving it here would silently
 * change every price chip on the site.
 */
function PriceForm({
  product,
  onApplied,
}: {
  product: AdminProductDetail;
  onApplied: (fresh: AdminProductDetail) => void;
}) {
  /**
   * Seeded from the product once, on mount. Same reasoning as `DetailsForm`:
   * this form is mounted per expanded row, so "seed from props" and "reset when
   * the product changes" are the same moment.
   *
   * Rupees on screen, paise on the wire. The conversion happens in one place
   * below and nowhere else.
   */
  const [price, setPrice] = useState(String(product.price.value));
  const [mrp, setMrp] = useState(String(product.mrp.value));
  const [label, setLabel] = useState(product.discountLabel);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const priceMinor = Math.round(Number(price) * 100);
  const mrpMinor = Math.round(Number(mrp) * 100);

  /**
   * The emptiness checks are not belt-and-braces. `Number("")` is `0`, so
   * without them clearing the price box and pressing save would publish a free
   * product — and the API would accept it, because zero is a legitimate price
   * as far as the schema is concerned.
   */
  const valid =
    price.trim() !== "" &&
    mrp.trim() !== "" &&
    Number.isFinite(priceMinor) &&
    Number.isFinite(mrpMinor) &&
    priceMinor >= 0 &&
    priceMinor <= mrpMinor &&
    label.trim() !== "";

  const changed =
    priceMinor !== product.price.minor ||
    mrpMinor !== product.mrp.minor ||
    label.trim() !== product.discountLabel;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || !changed || busy) return;

    setBusy(true);
    setError(null);

    try {
      onApplied(
        await products.updatePrice(product.slug, {
          priceMinor,
          mrpMinor,
          discountLabel: label.trim(),
          ...(note.trim() ? { note: note.trim() } : {}),
        }),
      );
      setNote("");
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "That price was not saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => void save(event)}
      className="rounded-lg border border-line bg-surface p-3"
    >
      <h3 className="mb-1 text-[11px] font-bold tracking-wide text-faint uppercase">Price</h3>
      <p className="mb-3 text-[11.5px] leading-relaxed text-faint">
        Saving appends to the price history. The old price is kept, never overwritten.
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Selling price (₹)">
          <input
            type="number"
            min={0}
            step={1}
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            className={input}
          />
        </Field>

        <Field label="MRP (₹)">
          <input
            type="number"
            min={0}
            step={1}
            value={mrp}
            onChange={(event) => setMrp(event.target.value)}
            className={input}
          />
        </Field>

        <Field label="Discount label" hint="From the sheet, not the arithmetic">
          <input
            type="text"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="25% off"
            className={input}
          />
        </Field>
      </div>

      <Field label="Why" hint="Recorded on the history row">
        <input
          type="text"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Festive pricing"
          className={input}
        />
      </Field>

      {priceMinor > mrpMinor ? (
        <p className="mt-2 text-[12px] text-bad">The selling price cannot be above the MRP.</p>
      ) : null}
      {error ? <p className="mt-2 text-[12px] text-bad">{error}</p> : null}

      <button
        type="submit"
        disabled={!valid || !changed || busy}
        className="mt-3 rounded-lg bg-ink px-3 py-2 text-[12.5px] font-semibold text-white disabled:opacity-40"
      >
        {busy ? "Saving…" : "Save price"}
      </button>
    </form>
  );
}
